/** Shared streak banner display rules (Bangkok calendar dates + time). */

export function getBangkokDateString(date = new Date()) {
  return date.toLocaleDateString("en-CA", { timeZone: "Asia/Bangkok" });
}

/** Bangkok wall-clock `YYYY-MM-DD HH:mm:ss` for campaign period checks. */
export function getBangkokDateTimeString(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Bangkok",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const pick = (type) => parts.find((part) => part.type === type)?.value ?? "";
  return `${pick("year")}-${pick("month")}-${pick("day")} ${pick("hour")}:${pick("minute")}:${pick("second")}`;
}

export function previousBangkokDateString(dateStr) {
  const date = new Date(`${dateStr}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

export function getStreakChallengeFromProfile(profile) {
  return (
    profile?.pointView?.streakChallenge ??
    profile?.pointView?.streak_challenge ??
    null
  );
}

/**
 * Parse campaign from/to as naive Bangkok `YYYY-MM-DD HH:mm:ss`.
 * ISO `...Z` is treated as stored local time (timestamp without time zone), not UTC.
 * Date-only values use 00:00:00 (from) or 23:59:59 (to).
 */
export function parseCampaignDateTime(value, { endOfDayIfDateOnly = false } = {}) {
  if (!value) {
    return null;
  }

  const raw = value instanceof Date ? value.toISOString() : String(value);
  const match = raw.match(
    /^(\d{4}-\d{2}-\d{2})(?:[T ](\d{2}:\d{2}:\d{2}))?/,
  );
  if (!match) {
    return null;
  }

  const time =
    match[2] ?? (endOfDayIfDateOnly ? "23:59:59" : "00:00:00");
  return `${match[1]} ${time}`;
}

export function isCampaignWindowActive(fromDate, toDate, now = new Date()) {
  const fromAt = parseCampaignDateTime(fromDate);
  const toAt = parseCampaignDateTime(toDate, { endOfDayIfDateOnly: true });
  if (!fromAt || !toAt) {
    return false;
  }

  const current = getBangkokDateTimeString(now);
  return current >= fromAt && current <= toAt;
}

function readStreakCampaignDateTime(
  streakChallenge,
  camelKey,
  snakeKey,
  { endOfDayIfDateOnly = false } = {},
) {
  const value = streakChallenge?.[camelKey] ?? streakChallenge?.[snakeKey];
  return parseCampaignDateTime(value, { endOfDayIfDateOnly });
}

/** Streak campaign อยู่ในช่วง fromDate–toDate (Bangkok date + time) */
export function isStreakCampaignInPeriod(streakChallenge, now = new Date()) {
  if (!streakChallenge) {
    return false;
  }

  const fromAt = readStreakCampaignDateTime(
    streakChallenge,
    "fromDate",
    "from_date",
  );
  const toAt = readStreakCampaignDateTime(
    streakChallenge,
    "toDate",
    "to_date",
    { endOfDayIfDateOnly: true },
  );

  if (!fromAt || !toAt) {
    return false;
  }

  const current = getBangkokDateTimeString(now);
  return current >= fromAt && current <= toAt;
}

export function shouldShowStreakChallengeBanner(profile) {
  return isStreakCampaignInPeriod(getStreakChallengeFromProfile(profile));
}

export function readStreakLastActivityDate(streakChallenge) {
  if (!streakChallenge) {
    return null;
  }

  const value =
    streakChallenge.lastActivityDate ??
    streakChallenge.last_activity_date ??
    null;

  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return value.slice(0, 10);
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  return null;
}

export function readStoredStreak(streakChallenge) {
  if (!streakChallenge) {
    return 0;
  }

  if (streakChallenge.storedStreak != null) {
    return Number(streakChallenge.storedStreak);
  }

  if (streakChallenge.stored_streak != null) {
    return Number(streakChallenge.stored_streak);
  }

  return Number(
    streakChallenge.currentStreak ?? streakChallenge.current_streak ?? 0,
  );
}

/** ค่า streak ใน DB เท่านั้น — ไม่ fallback currentStreak (display value) */
export function readStoredStreakFromDb(streakChallenge) {
  if (!streakChallenge) {
    return null;
  }

  if (streakChallenge.storedStreak != null) {
    return Number(streakChallenge.storedStreak);
  }

  if (streakChallenge.stored_streak != null) {
    return Number(streakChallenge.stored_streak);
  }

  return null;
}

/** ครบ 5 วันวันนี้ (ได้โบนัสแล้ว ยังไม่ขึ้นวันใหม่) */
export function isStreakFiveDayCompleteToday(streakChallenge) {
  if (!streakChallenge?.todayQualified) {
    return false;
  }

  const lastDate = readStreakLastActivityDate(streakChallenge);
  if (lastDate !== getBangkokDateString()) {
    return false;
  }

  const stored = readStoredStreakFromDb(streakChallenge);
  if (stored != null) {
    return stored === 0;
  }

  // payload เก่าที่ไม่มี storedStreak — ครบ 5 วันจะเป็น currentStreak 0 (API) หรือ 5 (หลัง normalize)
  const display = Number(
    streakChallenge.currentStreak ?? streakChallenge.current_streak ?? -1,
  );
  return display === 0 || display === 5;
}

/**
 * แสดง overlay "ได้รับรางวัลของวันนี้แล้ว" — เฉพาะครบ 5 วัน + ได้โบนัส 5 แต้มวันนี้
 * วันที่ 1–4 (ได้แค่ stamp) ไม่แสดง overlay
 */
export function shouldShowStreakTodayOverlay(streakChallenge) {
  return isStreakFiveDayCompleteToday(streakChallenge);
}

/** streak ขาดต่อเนื่อง — การ์ดว่าง */
export function isStreakContinuityBroken(streakChallenge) {
  if (isStreakFiveDayCompleteToday(streakChallenge)) {
    return false;
  }

  const stored = readStoredStreak(streakChallenge);
  const lastDate = readStreakLastActivityDate(streakChallenge);

  if (!lastDate) {
    // API เก่าอาจไม่ส่ง lastActivityDate — ถ้ามี streak แต่วันนี้ยังไม่สแกน ถือว่าขาด
    return stored > 0 && !streakChallenge?.todayQualified;
  }

  const today = getBangkokDateString();
  const yesterday = previousBangkokDateString(today);

  return lastDate !== today && lastDate !== yesterday;
}

/** จำนวน stamp ที่ควรแสดงบน banner */
export function computeStreakBannerStampCount(streakChallenge) {
  if (!streakChallenge) {
    return 0;
  }

  if (isStreakFiveDayCompleteToday(streakChallenge)) {
    return 5;
  }

  if (isStreakContinuityBroken(streakChallenge)) {
    return 0;
  }

  return Math.min(Math.max(readStoredStreak(streakChallenge), 0), 5);
}

export function normalizeStreakChallenge(streakChallenge) {
  if (!streakChallenge) {
    return null;
  }

  const storedStreak = readStoredStreak(streakChallenge);
  const displayStreak = computeStreakBannerStampCount({
    ...streakChallenge,
    storedStreak,
    currentStreak: storedStreak,
  });

  return {
    ...streakChallenge,
    storedStreak,
    currentStreak: displayStreak,
    lastActivityDate: readStreakLastActivityDate(streakChallenge),
    todayQualified: Boolean(streakChallenge.todayQualified),
  };
}
